import React from "react";
import moment from "moment";
import {
  formatMelbourneDayLabel,
  formatMelbourneDateTime,
} from "../../../utils/melbourneTime";
import { Bed, Bath, Car, Clock, User, Pencil, StickyNote, Thermometer, Database, CheckCircle2 } from "lucide-react";
import MarkLostMenu from "./MarkLostMenu";
import PublicMatchBadge from "../../properties/components/PublicMatchBadge";
import { withdrawnReasonLabel } from "../constants/lostReasons";

const formatPrice = (price) => {
    if (price == null || price === "") return null;
    const num = Number(price);
    if (Number.isNaN(num)) return price;
    return `$${Math.round(num).toLocaleString()}`;
};

const getPriceTypeLabel = (priceDisplay) => {
    const labels = { exact: "Asking Price", range: "Price Range", contact: "Contact Agent", auction: "Guided Price" };
    return labels[priceDisplay] || "Asking Price";
};

/**
 * Card for appraisal property in On/Off Market pipeline.
 * Same info as PropertyCard (except image): title/address, price, beds/baths/parking, yield, method of sale, plus primary agent and expected commission.
 */
const PipelinePropertyCard = ({ property, theme, onClick, onEdit, onChangeTemperature, onAddToDatabase, onListed, onLost }) => {
    const address = property.address || [property.street_name, property.suburb, property.state].filter(Boolean).join(", ") || property.title || "Property Address";
    const primaryAgent = property.agent;
    const agentName = primaryAgent?.name || "—";

    const priceVal = property.price != null ? parseFloat(property.price) : null;
    const priceTo = property.price_to != null ? parseFloat(property.price_to) : null;
    const priceDisplay = property.price_display || "exact";

    const getPriceDisplay = () => {
        if (priceDisplay === "range" && priceVal != null && priceTo != null && Number(priceTo) !== Number(priceVal)) {
            return `${getPriceTypeLabel(priceDisplay)}: ${formatPrice(priceVal)} – ${formatPrice(priceTo)}`;
        }
        if (priceDisplay === "contact") return getPriceTypeLabel(priceDisplay);
        if (priceDisplay === "auction" && priceVal != null) return `${getPriceTypeLabel(priceDisplay)}: ${formatPrice(priceVal)}`;
        if (priceVal != null) return `${getPriceTypeLabel(priceDisplay)}: ${formatPrice(priceVal)}`;
        return `${getPriceTypeLabel(priceDisplay)}: Contact Agent`;
    };

    const price = parseFloat(property.price) || 0;
    const rate = property.commission_rate != null ? parseFloat(property.commission_rate) : null;
    const amount = property.commission_amount != null ? parseFloat(property.commission_amount) : null;
    const estimatedCommission = amount != null && amount > 0 ? amount : (rate != null && rate > 0 && price > 0 ? (rate / 100) * price : 0);
    const timeAgo = property.created_at ? moment(property.created_at).fromNow() : "—";

    const method = (property.method_of_sale || "Private Sale").trim();
    const isAuction = method.toLowerCase() === "auction";
    const methodLabel = isAuction && property.auction_date ? `AUCTION ${formatMelbourneDayLabel(property.auction_date)}` : method;

    const hasRent = property.weekly_rent != null && property.weekly_rent !== "";

    // Newest PropertyNote, served by the listing endpoint as `latest_note`.
    const latestNote = property.latest_note;

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onClick?.(property)}
            onKeyDown={(e) => e.key === "Enter" && onClick?.(property)}
            className="bg-white rounded-xl border border-[#F3F4F6] p-3 sm:p-4 shadow-sm hover:shadow-md transition-all cursor-pointer group font-sans text-[#353B48]"
        >
            <div className="flex flex-col gap-2 sm:gap-2.5">
                {/* Lost badge - shows on the Lost column so the reason is visible
                    at a glance (Michael Piccoli, Monday 13090956131). */}
                {property.is_withdrawn && (
                    <div className="flex">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-[#FDECEC] text-[#DC2626] border border-[#E5484D]/30 uppercase tracking-wide">
                            Lost{property.withdrawn_reason ? ` · ${withdrawnReasonLabel(property.withdrawn_reason)}` : ""}
                            {property.withdrawn_at ? ` · ${moment(property.withdrawn_at).format("D MMM YYYY")}` : ""}
                        </span>
                    </div>
                )}
                {/* Scheduled Launch Badge */}
                {property.listing_launch_at && new Date(property.listing_launch_at) > new Date() && (
                    <div className="flex">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold bg-[#55C5D0]/10 text-[#55C5D0] border border-[#55C5D0]/20 animate-pulse">
                            🕐 SCHEDULED: {formatMelbourneDateTime(property.listing_launch_at)}
                        </span>
                    </div>
                )}
                {/* Public Property match flag (DOMA 13121348574): only shows when
                    the property could not be matched and needs resolution. */}
                {["unmatched", "needs_review"].includes(property.public_match_status) && (
                    <div className="flex">
                        <PublicMatchBadge status={property.public_match_status} />
                    </div>
                )}
                {/* Title / Address (same as PropertyCard) + quick-edit button */}
                <div className="flex items-start justify-between gap-2">
                    <h3 className="text-[13px] sm:text-[14px] font-bold text-[#333333] line-clamp-2 leading-snug">
                        {address}
                    </h3>
                    <div className="flex items-center shrink-0 -mr-1 -mt-1">
                        {/* Dragging the card is the fast way to move it between
                            columns, but it records no reason and is fiddly on a
                            phone, so the same Move Appraisal dialog the list
                            rows use is here too (Simon Abbott, Monday
                            12985995388). */}
                        {onChangeTemperature && (
                            <button
                                type="button"
                                title="Move to Cold / Warm / Hot"
                                aria-label="Move this appraisal to Cold, Warm or Hot"
                                onClick={(e) => { e.stopPropagation(); onChangeTemperature(property); }}
                                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F89542] hover:bg-[#F89542]/10 transition-colors"
                            >
                                <Thermometer className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {/* The round trip only went one way: a Database row
                            could become an appraisal, but an appraisal that
                            never became a listing had no way back onto a
                            calling list (Paul Jimoulis, CRM-2026-00070). This
                            asks which tab it goes onto. */}
                        {onAddToDatabase && (
                            <button
                                type="button"
                                title="Add this appraisal to your Database"
                                aria-label="Add this appraisal to your Database"
                                onClick={(e) => { e.stopPropagation(); onAddToDatabase(property); }}
                                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
                            >
                                <Database className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {onEdit && (
                            <button
                                type="button"
                                title="Edit appraisal details"
                                aria-label="Edit appraisal details"
                                onClick={(e) => { e.stopPropagation(); onEdit(property); }}
                                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#55C5D0] hover:bg-[#55C5D0]/10 transition-colors"
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {/* Listed — promote to a draft listing in My Listings. */}
                        {onListed && (
                            <button
                                type="button"
                                title="Mark as Listed (moves to My Listings as a draft)"
                                aria-label="Mark as Listed"
                                onClick={(e) => { e.stopPropagation(); onListed(property); }}
                                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#2b9aa4] hover:bg-[#55C5D0]/10 transition-colors"
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {/* Lost — same reason list/effect as the holding area. */}
                        {onLost && (
                            <div onClick={(e) => e.stopPropagation()}>
                                <MarkLostMenu
                                    onSelect={(reason, note) => onLost(property, reason, note)}
                                    label=""
                                    className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#c8392f] hover:bg-[#c8392f]/10 transition-colors"
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Price + Weekly Rent */}
                <div className="min-h-[28px] flex flex-col gap-0.5">
                    <p className="text-[11px] sm:text-[12px] font-bold text-[#5F6368] line-clamp-2">{getPriceDisplay()}</p>
                    {hasRent && (
                        <p className="text-[10px] sm:text-[11px] font-medium text-gray-600">
                            Weekly Rent: {formatPrice(property.weekly_rent)}
                        </p>
                    )}
                </div>

                {/* Most recent note on the appraisal, stamped with who wrote it
                    and when, so the last thing said about it is readable from
                    the board (Simon Abbott, Monday 12953887002). Written on the
                    Add Appraisal form, the quick-edit modal, or the property's
                    Notes tab. */}
                {latestNote?.content && (
                    <div className="flex items-start gap-1.5 rounded-md bg-amber-50 border border-amber-100 px-2 py-1.5">
                        <StickyNote className="w-3 h-3 text-amber-500 mt-0.5 shrink-0" />
                        <div className="min-w-0">
                            <p className="text-[11px] text-gray-700 whitespace-pre-wrap break-words line-clamp-3">
                                {latestNote.content}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-0.5">
                                {latestNote.author_name || "Unknown"}
                                {latestNote.created_at ? ` · ${formatMelbourneDateTime(latestNote.created_at)}` : ""}
                            </p>
                        </div>
                    </div>
                )}

                {/* Beds, Baths, Parking */}
                <div className="flex items-center gap-4 py-0.5">
                    <div className="flex items-center gap-1.5 text-[#5F6368]">
                        <Bed size={16} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[12px] font-medium">{property.beds ?? 0}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#5F6368]">
                        <Bath size={16} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[12px] font-medium">{property.baths ?? 0}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#5F6368]">
                        <Car size={16} strokeWidth={1.5} className="text-[#323D3D]" />
                        <span className="text-[12px] font-medium">{
                            (Number(property.parkings) || 0)
                            + (Number(property.car_ports) || 0)
                            + (Number(property.open_spaces) || 0)
                            + (Number(property.parking_spaces) || 0)
                            || (Number(property.parking) || 0)
                        }</span>
                    </div>
                </div>

                <div className="h-[1px] bg-gray-200 w-full" />

                {/* Yield (same as PropertyCard – N/A when no weekly rent) */}
                <div className="grid grid-cols-3 gap-1 py-0.5">
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-[#5F6368] font-bold capitalize">Est. Gross Yield</span>
                        <span className="text-[12px] font-bold text-[#00A859]">
                            {hasRent && property.est_gross_yield ? `${parseFloat(property.est_gross_yield).toFixed(1)}%` : "N/A"}
                        </span>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-[#5F6368] font-bold capitalize">Est. Outgoings P.A.</span>
                        <span className="text-[12px] font-bold text-[#2DC8DB]">
                            {hasRent && property.est_outgoings ? formatPrice(property.est_outgoings) : "N/A"}
                        </span>
                    </div>
                    <div className="flex flex-col items-center">
                        <span className="text-[9px] text-[#5F6368] font-bold capitalize">Est. Net Yield</span>
                        <span className="text-[12px] font-bold text-[#2DC8DB]">
                            {hasRent && property.est_net_yield ? `${parseFloat(property.est_net_yield).toFixed(1)}%` : "N/A"}
                        </span>
                    </div>
                </div>

                {/* Primary Agent */}
                <div className="flex items-center gap-2 pt-0.5">
                    <User className="w-3.5 h-3.5 text-[#55C5D0] shrink-0" />
                    <span className="text-[10px] text-[#5F6368] font-bold uppercase tracking-wide shrink-0">Primary Agent:</span>
                    <div className="flex items-center gap-1.5 min-w-0">
                        <div className="w-5 h-5 rounded-full bg-[#55C5D0] flex items-center justify-center text-white text-[9px] font-bold shrink-0">
                            {agentName.charAt(0).toUpperCase() || "—"}
                        </div>
                        <span className="text-[11px] font-medium truncate">{agentName}</span>
                    </div>
                </div>

                {/* Expected Commission (pipeline-specific) */}
                <div className={`${theme.commBg} py-2 px-2.5 rounded-lg flex items-center justify-between gap-2`}>
                    <span className="text-[#4B5563] font-medium text-[10px] leading-[100%] whitespace-nowrap">Expected Commission</span>
                    <span className={`font-bold text-[11px] leading-[100%] ${theme.text} whitespace-nowrap`}>
                        ${estimatedCommission > 0 ? Math.round(estimatedCommission).toLocaleString() : "—"}
                    </span>
                </div>

                {/* Method of Sale + Time */}
                <div className="flex items-center justify-between pt-0.5 border-t border-gray-100">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide">{methodLabel}</span>
                    <div className="flex items-center gap-1 text-[#9CA3AF] text-[10px] font-medium">
                        <Clock className="w-3 h-3 flex-shrink-0" />
                        <span>{timeAgo}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PipelinePropertyCard;
